/**
 * Utility functions for search suggestions.
 * Pure functions without side effects.
 */

import { SearchSuggestion } from './types';

/**
 * Check if text matches query according to matching rules.
 * Case-insensitive, supports includes and optional startsWith.
 *
 * @param text Text to check for match
 * @param query Search query (should be trimmed and lowercase)
 * @param includeStartsWith Whether to prioritize startsWith matches
 * @returns true if text matches query
 */
export function matchesQuery(
  text: string,
  query: string,
  includeStartsWith: boolean = true
): boolean {
  if (!query || query.length < 1) return false;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  // Try startsWith first if enabled (better match)
  if (includeStartsWith && lowerText.startsWith(lowerQuery)) {
    return true;
  }

  // Fall back to includes
  return lowerText.includes(lowerQuery);
}

/**
 * Remove duplicate suggestions (case-insensitive).
 * Preserves first occurrence of each unique suggestion.
 *
 * @param suggestions Array of suggestion strings
 * @returns Deduplicated array
 */
export function deduplicateSuggestions(suggestions: SearchSuggestion[]): SearchSuggestion[] {
  const seen = new Set<string>();
  const result: SearchSuggestion[] = [];

  for (const suggestion of suggestions) {
    const lowerSuggestion = suggestion.toLowerCase();
    if (!seen.has(lowerSuggestion)) {
      seen.add(lowerSuggestion);
      result.push(suggestion);
    }
  }

  return result;
}

/**
 * Limit suggestions array to maximum number.
 *
 * @param suggestions Array of suggestion strings
 * @param max Maximum number of suggestions to return
 * @returns Limited array (first N items)
 */
export function limitSuggestions(
  suggestions: SearchSuggestion[],
  max: number
): SearchSuggestion[] {
  return suggestions.slice(0, max);
}

/**
 * Extract short phrase from product title (2-4 words).
 * Used to avoid showing overly long product titles as suggestions.
 *
 * Rules:
 * - Take first 2-4 words (prefer 3)
 * - Skip very short words (<=2 chars) unless necessary
 * - Preserve original capitalization
 *
 * @param title Full product title
 * @returns Shortened phrase (2-4 words)
 */
export function truncateProductTitle(title: string): string {
  if (!title || title.length === 0) return '';

  const words = title.split(/\s+/);

  // If title is already short (<=4 words), return as-is
  if (words.length <= 4) {
    return title;
  }

  // Take first 3 words as default (balanced length)
  // Skip very short words (<=2 chars) at the beginning
  let resultWords: string[] = [];
  let wordCount = 0;

  for (const word of words) {
    if (wordCount >= 3) break; // Target 3 words

    // Skip very short words unless we need them
    if (word.length <= 2 && wordCount > 0) {
      continue;
    }

    resultWords.push(word);
    wordCount++;
  }

  // Ensure we have at least 2 words
  if (resultWords.length < 2 && words.length >= 2) {
    resultWords = words.slice(0, 2);
  }

  // Ensure we don't exceed 4 words
  if (resultWords.length > 4) {
    resultWords = resultWords.slice(0, 4);
  }

  return resultWords.join(' ');
}

/**
 * Validate query meets minimum length requirement.
 *
 * @param query Search query
 * @param minQueryLength Minimum required length (default: 2)
 * @returns true if query is valid for suggestion generation
 */
export function isValidQuery(
  query: string,
  minQueryLength: number = 2
): boolean {
  const trimmed = query.trim();
  return trimmed.length >= minQueryLength;
}